import { useVirtualizer } from '@tanstack/react-virtual';
import isEqual from 'lodash/isEqual';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Button from '@/core/components/Button';
import type { CatalogueSearchSchema } from '@/core/components/CatalogueSearch/CatalogueSearchProvider';
import { useCatalogueSearchContext } from '@/core/components/CatalogueSearch/CatalogueSearchProvider';
import DonationCard from '@/core/components/DonationCard';
import { useIsLg } from '@/core/hooks/useMediaQuery';
import { BannerSlot, useAdsContext } from '@/core/lib/ads/ads.context';
import { LIST_ITEM_ADD_POSITION, NATIVE_ADD_POSITION, TEMPORARY_LIST_ITEM_ADD_POSITION } from '@/core/lib/constants';
import { useEnvContext } from '@/core/lib/env/env.context';
import { useFFContext } from '@/core/lib/ff/ff.context';
import { useNativeContext } from '@/core/lib/native/native.context';
import type DonationDomain from '@/core/lib/new-architecture/domain/donation.domain';
import { useScrollContext } from '@/core/lib/scroll/scroll.context';
import { useTranslationContext } from '@/core/lib/translation/translation.context';

interface ItemListProps {
  donations: DonationDomain[];
  search: CatalogueSearchSchema;
  hasNext: boolean;
  shouldFetchNext: boolean;
  total: number;
  next: () => void;
}

const ItemList: React.FC<ItemListProps> = ({ donations, search, hasNext, shouldFetchNext, total, next }) => {
  const { t } = useTranslationContext(['common', 'catalogue']);
  const { featureFlag } = useFFContext();
  const { isNative } = useNativeContext();
  const { Ad } = useAdsContext();
  const { onOpen } = useCatalogueSearchContext();
  const isLg = useIsLg();
  const { env } = useEnvContext();

  const parentRef = useRef<HTMLDivElement>(null);

  const { set, get } = useScrollContext();

  const getItemKey = useCallback(
    (index: number) => {
      const donation = donations[index];

      if (!donation) return '';

      return `${index}-${donation.getId()}`;
    },
    [donations],
  );

  const [scroll, setScroll] = useState({ id: JSON.stringify(search), offset: get(JSON.stringify(search)) });

  useEffect(() => {
    const id = JSON.stringify(search);
    setScroll({ id, offset: get(id) });
  }, [search]);

  const virtualizer = useVirtualizer({
    count: hasNext ? donations.length + 1 : donations.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 400,
    overscan: 5,
    getItemKey,
    lanes: 2,
    initialOffset: () => get(scroll.id) ?? 0,
    onChange: vir => set(scroll.id, vir.scrollOffset || 0),
  });

  useEffect(() => {
    if (scroll.id && scroll.offset) {
      virtualizer.scrollToOffset(scroll.offset);
    }
  }, [scroll]);

  useEffect(() => {
    const [lastItem] = [...virtualizer.getVirtualItems()].reverse();

    if (!lastItem) {
      return;
    }

    if (lastItem.index >= donations.length - 1 && shouldFetchNext) {
      next();
    }
  }, [shouldFetchNext, next, virtualizer.getVirtualItems()]);

  const render = useMemo(() => {
    const renderList: React.ReactNode[] = [];

    donations.forEach((donation, index) => {
      renderList.push(
        <div key={donation.getId()} className="col-span-1">
          <DonationCard donation={donation} vertical />
        </div>,
      );

      const showNativeAd = featureFlag?.yieldloveNativeAds.web[env];

      const listItemAddPosition = showNativeAd ? LIST_ITEM_ADD_POSITION : TEMPORARY_LIST_ITEM_ADD_POSITION;
      const showNativeAdEveryNineDonationsOnSixteen = index > 0 && (index + 1) % listItemAddPosition === NATIVE_ADD_POSITION;
      const showListItemAd = index > 0 && (index + 1) % listItemAddPosition === 0;

      if (showNativeAdEveryNineDonationsOnSixteen && showNativeAd) {
        const divId = isLg ? `${donation.getId()}-ad-native-desktop` : `${donation.getId()}-ad-native-mobile`;
        const size = isLg ? '728x480' : '336x280';

        renderList.push(
          <div key={divId} className="col-span-1">
            <Ad size={size} slot={BannerSlot.DONNONS_NATIVE} clone={(index + 1) / NATIVE_ADD_POSITION - 1} />
          </div>,
        );
      }

      if (showListItemAd) {
        const divId = isLg ? `${donation.getId()}-ad-desktop` : `${donation.getId()}-ad-mobile`;
        const media = isLg ? 'lg' : undefined;
        const size = isLg ? '728x480' : '336x280';

        renderList.push(
          <div key={divId} className="col-span-2 flex items-center justify-center lg:col-span-4">
            <Ad size={size} slot={BannerSlot.DONNONS_LIST_ITEM} clone={(index + 1) / listItemAddPosition - 1} media={media} isMobile={!isLg} />
          </div>,
        );
      }
    });

    return renderList;
  }, [donations, isLg, featureFlag]);

  // handle reset scroll when new search is made on web
  const prevSearch = useRef<CatalogueSearchSchema | null>(null);
  useEffect(() => {
    if (prevSearch.current === null) {
      prevSearch.current = search;
    } else if (!isEqual(prevSearch.current, search)) {
      prevSearch.current = search;
      window.document.scrollingElement?.scrollTo({ top: 0 });
    }
  }, [search]);

  if (render.length === 0) {
    return (
      <div className="flex w-full justify-center">
        <div className="flex h-full w-100 flex-col items-center justify-center">
          <img src="/assets/empty/search.svg" className="size-45" alt={t('not-found.title', { ns: 'catalogue' })} />
          <h2 className="text-body-secondary mb-2 font-semibold text-content-primary">{t('not-found.title', { ns: 'catalogue' })}</h2>
          <p className="text-body-secondary mb-7 text-center font-normal text-content-placeholder">{t('not-found.subtitle', { ns: 'catalogue' })}</p>
          <div className="flex w-full flex-col space-y-20">
            <Button variant="secondary" onClick={onOpen} iconLeft="sliders">
              {t('not-found.again', { ns: 'catalogue' })}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isNative) {
    return (
      <div ref={parentRef} className="flex w-full overflow-y-auto" style={{ flex: 1 }}>
        <div className="relative w-full" style={{ height: `${virtualizer.getTotalSize()}px` }}>
          {virtualizer.getVirtualItems().map(virtualItem => {
            const isLoaderRow = virtualItem.index > total - 1;
            const donation = donations[virtualItem.index];

            if (!donation || isLoaderRow) {
              return null;
            }

            return (
              <div
                key={virtualItem.key}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: `${virtualItem.lane * 50}%`,
                  width: '50%',
                  height: `${virtualItem.size}px`,
                  transform: `translateY(${virtualItem.start}px)`,
                }}
                className={`${virtualItem.index % 2 ? 'pl-[6px]' : 'pr-[6px]'}`}
              >
                <DonationCard donation={donation} vertical />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return <div className="grid w-full grid-cols-2 gap-x-3 gap-y-5 lg:grid-cols-4">{render}</div>;
};

export default ItemList;
